import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import DiscountCodeModal from './DiscountCodeModal';

const formatDate = (d) => {
  if (!d) return 'Never';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return 'Never';
  return date.toLocaleDateString();
};

const DiscountsSettings = () => {
  const { studio, refreshUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const familyCodes = studio?.familyDiscountCodes || [];
  const enabled = !!studio?.discountsEnabled;

  const handleToggle = async () => {
    setSaving(true);
    setError('');
    try {
      await api.put('/studio', { discountsEnabled: !enabled });
      await refreshUser();
      setMessage(!enabled ? 'Discount codes enabled.' : 'Discount codes disabled.');
      setTimeout(() => setMessage(''), 2500);
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerate = async (data) => {
    await api.post('/studio/family-discount-codes', data);
    await refreshUser();
    setMessage(`Code "${data.code}" created.`);
    setTimeout(() => setMessage(''), 2500);
  };

  const handleDeactivate = async (codeId) => {
    if (!window.confirm('Deactivate this code? Already-redeemed enrollments are not affected.')) return;
    setError('');
    try {
      await api.delete(`/studio/family-discount-codes/${codeId}`);
      await refreshUser();
      setMessage('Code deactivated.');
      setTimeout(() => setMessage(''), 2500);
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to deactivate');
    }
  };

  const handleReactivate = async (codeId) => {
    setError('');
    try {
      await api.patch(`/studio/family-discount-codes/${codeId}`, { active: true });
      await refreshUser();
      setMessage('Code reactivated.');
      setTimeout(() => setMessage(''), 2500);
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to reactivate');
    }
  };

  return (
    <div className="space-y-6">
      {message && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">{message}</div>
      )}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>
      )}

      {/* Central kill-switch */}
      <div className="bg-white p-5 sm:p-6 rounded-xl border border-gray-100 shadow-sm">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">Accept discount codes</h2>
            <p className="text-sm text-gray-500 mt-1 max-w-xl">
              When off, students won't see a discount-code field on any enrollment page,
              regardless of per-course settings.
            </p>
          </div>
          <button
            type="button"
            onClick={handleToggle}
            disabled={saving}
            className={`shrink-0 relative inline-flex h-7 w-12 items-center rounded-full border transition-colors disabled:opacity-50 ${
              enabled
                ? 'bg-brand-500 border-brand-500'
                : 'bg-gray-200 border-gray-300'
            }`}
            aria-pressed={enabled}
            aria-label={enabled ? 'Discount codes enabled' : 'Discount codes disabled'}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-1 ring-black/5 transition-transform ${
                enabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Family codes table */}
      <div className="bg-white p-5 sm:p-6 rounded-xl border border-gray-100 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
          <div className="min-w-0 flex-1">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">Studio-wide family codes</h2>
            <p className="text-sm text-gray-500 mt-1">
              Any active course in this studio can accept these codes during enrollment.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            disabled={!enabled}
            className="shrink-0 self-start sm:self-auto bg-brand-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-brand-600 disabled:bg-gray-300 whitespace-nowrap"
            title={!enabled ? 'Enable discount codes first' : ''}
          >
            <span className="sm:hidden">+ New code</span>
            <span className="hidden sm:inline">Generate family code</span>
          </button>
        </div>

        {!enabled && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
            Discount codes are turned off — students won't see codes on the enrollment page until you enable the toggle above.
          </div>
        )}

        {familyCodes.length === 0 ? (
          <p className="text-sm text-gray-500">No family codes yet. Click "Generate family code" to create one.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b border-gray-200">
                  <th className="py-2 pr-4">Code</th>
                  <th className="py-2 pr-4">Amount off</th>
                  <th className="py-2 pr-4">Expires</th>
                  <th className="py-2 pr-4">Redemptions</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {familyCodes.map(c => {
                  const expired = c.expiresAt && new Date(c.expiresAt).getTime() < Date.now();
                  const exhausted = c.maxRedemptions != null && c.redemptions >= c.maxRedemptions;
                  const status = !c.active ? 'Inactive' : expired ? 'Expired' : exhausted ? 'Exhausted' : 'Active';
                  const statusColor = status === 'Active' ? 'text-green-700 bg-green-50' :
                    status === 'Inactive' ? 'text-gray-500 bg-gray-100' :
                    'text-amber-700 bg-amber-50';
                  return (
                    <tr key={c._id} className="text-gray-700">
                      <td className="py-3 pr-4 font-mono font-medium text-gray-900">{c.code}</td>
                      <td className="py-3 pr-4">${c.amountOff}</td>
                      <td className="py-3 pr-4">{formatDate(c.expiresAt)}</td>
                      <td className="py-3 pr-4">
                        {c.redemptions || 0}{c.maxRedemptions != null ? ` / ${c.maxRedemptions}` : ''}
                      </td>
                      <td className="py-3 pr-4">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${statusColor}`}>
                          {status}
                        </span>
                      </td>
                      <td className="py-3 pr-4 text-right">
                        {c.active ? (
                          <button
                            type="button"
                            onClick={() => handleDeactivate(c._id)}
                            className="text-sm text-red-600 hover:text-red-800 font-medium"
                          >
                            Deactivate
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleReactivate(c._id)}
                            className="text-sm text-brand-500 hover:text-brand-600 font-medium"
                          >
                            Reactivate
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <DiscountCodeModal
        open={modalOpen}
        title="Generate family code"
        existingCodes={familyCodes}
        onClose={() => setModalOpen(false)}
        onSubmit={handleGenerate}
      />
    </div>
  );
};

export default DiscountsSettings;
